"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { Header } from "@/modules/shared/components/Header";
import { Footer } from "@/modules/shared/components/Footer";
import { PageTransition, FadeIn } from "@/modules/shared/components/PageTransition";
import { siteConfig } from "@/config/site.config";

export default function EscuelaFilosofiaClient() {
  // Encuesta de prioridades
  const [votoSeleccionado, setVotoSeleccionado] = useState<string | null>(null);
  const [haVotado, setHaVotado] = useState(false);
  const [conteoVotos, setConteoVotos] = useState<Record<string, number>>({});
  const [totalVotos, setTotalVotos] = useState(0);

  // Buzón de propuestas
  const [propuesta, setPropuesta] = useState("");
  const [ciclo, setCiclo] = useState("");
  const [esAnonimo, setEsAnonimo] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [buzonStatus, setBuzonStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [buzonError, setBuzonError] = useState("");

  // Cargar voto previo de localStorage
  useEffect(() => {
    try {
      const previo = localStorage.getItem("copleston_voto_filosofia_unsaac");
      const storedCounts = localStorage.getItem("copleston_conteo_filosofia_unsaac");
      if (previo) {
        setVotoSeleccionado(previo);
        setHaVotado(true);
      }
      if (storedCounts) {
        const parsed = JSON.parse(storedCounts);
        setConteoVotos(parsed);
        const sum = Object.values(parsed).reduce((acc: number, val) => acc + (typeof val === "number" ? val : 0), 0);
        setTotalVotos(sum);
      }
    } catch {
      // Ignorar errores de almacenamiento local
    }
  }, []);

  const handleVotar = (opcionId: string) => {
    if (haVotado) return;

    const nuevoConteo = { ...conteoVotos, [opcionId]: (conteoVotos[opcionId] || 0) + 1 };
    setConteoVotos(nuevoConteo);
    setTotalVotos((prev) => prev + 1);
    setVotoSeleccionado(opcionId);
    setHaVotado(true);

    try {
      localStorage.setItem("copleston_voto_filosofia_unsaac", opcionId);
      localStorage.setItem("copleston_conteo_filosofia_unsaac", JSON.stringify(nuevoConteo));
    } catch {
      // Ignorar
    }

    // Registrar voto en endpoint real de recolección
    try {
      const formData = new FormData();
      formData.append("opcion_votada", opcionId);
      formData.append("_subject", `Voto en Encuesta Filosofía UNSAAC: ${opcionId}`);
      formData.append("_captcha", "false");
      fetch(`https://formsubmit.co/ajax/${siteConfig.correoContacto}`, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      }).catch(() => {});
    } catch {
      // Silencioso
    }
  };

  const handleEnviarPropuesta = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    if (!propuesta.trim()) {
      setBuzonError("Por favor escribe tu propuesta o problema.");
      return;
    }

    setBuzonStatus("loading");
    setBuzonError("");

    try {
      const formData = new FormData();
      formData.append("propuesta", propuesta);
      formData.append("ciclo", ciclo || "No especificado");
      formData.append("modalidad", esAnonimo ? "Anónimo" : "Identificado");
      formData.append("_subject", "Nueva Propuesta/Buzón: Escuela de Filosofía UNSAAC");
      formData.append("_captcha", "false");

      const res = await fetch(`https://formsubmit.co/ajax/${siteConfig.correoContacto}`, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });

      if (res.ok) {
        setBuzonStatus("success");
        setPropuesta("");
        setCiclo("");
      } else {
        setBuzonStatus("error");
        setBuzonError("Ocurrió un error al enviar. Por favor intenta nuevamente.");
      }
    } catch {
      setBuzonStatus("error");
      setBuzonError("Error de conexión. Intenta de nuevo más tarde.");
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-bg-main text-text-pure flex flex-col font-sans selection:bg-gold/30 selection:text-gold-light">
        <Header />

        <main className="flex-grow pt-24 md:pt-32 pb-20">
          
          {/* HERO */}
          <section className="relative w-full min-h-[50vh] flex items-center justify-center px-6 md:px-12 mb-16 overflow-hidden">
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(197,160,89,0.1),transparent_70%)] pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-b from-bg-main/60 via-transparent to-bg-main" />
            </div>

            <div className="relative z-10 text-center max-w-4xl mx-auto pt-6">
              <FadeIn>
                <div className="inline-flex items-center gap-2 border border-gold/35 bg-gold/5 px-4 py-1 rounded-full mb-6">
                  <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                  <span className="text-[10px] md:text-xs font-mono tracking-widest text-gold uppercase font-medium">
                    UNSAAC · Cusco
                  </span>
                </div>

                <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl tracking-wide font-light mb-6">
                  {siteConfig.escuelaFilosofia.heroTitle}
                </h1>

                <p className="text-text-muted/95 text-base sm:text-lg md:text-xl max-w-2xl mx-auto font-light leading-relaxed mb-6">
                  {siteConfig.escuelaFilosofia.heroSubtitle}
                </p>

                <p className="text-xs font-mono text-gold/70 tracking-widest uppercase">
                  Voz libre, participación directa y rigor académico
                </p>
              </FadeIn>
            </div>
          </section>

          <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-24">
            
            {/* MI COMPROMISO */}
            <FadeIn delay={0.1}>
              <section className="p-8 sm:p-12 rounded-2xl border border-gold/25 bg-bg-secondary/30 relative overflow-hidden shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-[1px] bg-gold/50" />
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Declaración
                  </span>
                </div>

                <h2 className="font-serif text-2xl sm:text-3xl text-text-pure font-light mb-6">
                  Mi Compromiso con la Escuela
                </h2>

                <blockquote className="border-l-2 border-gold/40 pl-6 my-2">
                  <p className="text-text-pure/85 text-base sm:text-lg font-light leading-relaxed tracking-wide">
                    &ldquo;{siteConfig.escuelaFilosofia.compromiso}&rdquo;
                  </p>
                </blockquote>

                <p className="text-right text-xs font-mono text-gold/80 mt-4 tracking-wider uppercase">
                  — {siteConfig.fundador.nombre}
                </p>
              </section>
            </FadeIn>

            {/* ENCUESTA DE PRIORIDADES EN VIVO */}
            <FadeIn delay={0.2}>
              <section id="encuesta" className="p-8 sm:p-12 rounded-2xl border border-gold/20 bg-[#070912]">
                <div className="text-center flex flex-col items-center mb-10">
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Consulta Estudiantil
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl text-text-pure mt-3 font-light">
                    Encuesta de Prioridades
                  </h2>
                  <p className="text-text-muted text-xs sm:text-sm font-light max-w-xl mt-2 leading-relaxed">
                    1 voto por navegador con resultados transparentes en vivo. ¿Qué aspecto consideras más urgente priorizar en nuestra Escuela?
                  </p>
                  <div className="w-12 h-[1px] bg-gold/50 mt-4" />
                </div>

                {totalVotos === 0 && !haVotado ? (
                  <p className="text-center text-xs font-mono text-gold/80 mb-6 tracking-widest uppercase">
                    Sé el primero en votar
                  </p>
                ) : (
                  <p className="text-center text-xs font-mono text-text-muted mb-6 tracking-widest uppercase">
                    {totalVotos} {totalVotos === 1 ? "voto registrado" : "votos registrados"}
                  </p>
                )}

                <div className="space-y-4 max-w-3xl mx-auto">
                  {siteConfig.escuelaFilosofia.opcionesEncuesta.map((opcion) => {
                    const votosOpcion = conteoVotos[opcion.id] || 0;
                    const porcentaje = totalVotos > 0 ? Math.round((votosOpcion / totalVotos) * 100) : 0;
                    const esMiVoto = votoSeleccionado === opcion.id;

                    return (
                      <div
                        key={opcion.id}
                        onClick={() => !haVotado && handleVotar(opcion.id)}
                        className={`relative p-5 rounded-xl border transition-all duration-300 ${
                          haVotado
                            ? esMiVoto
                              ? "border-gold bg-gold/10"
                              : "border-gold/15 bg-bg-secondary/20 cursor-default"
                            : "border-gold/20 bg-bg-secondary/25 hover:border-gold hover:bg-gold/5 cursor-pointer"
                        }`}
                      >
                        {/* Barra de progreso si ya votó */}
                        {haVotado && (
                          <div
                            className="absolute top-0 left-0 bottom-0 bg-gold/10 rounded-xl transition-all duration-500 pointer-events-none"
                            style={{ width: `${porcentaje}%` }}
                          />
                        )}

                        <div className="relative z-10 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <span
                              className={`w-4 h-4 rounded-full border flex items-center justify-center text-[9px] ${
                                esMiVoto
                                  ? "border-gold bg-gold text-bg-main"
                                  : "border-gold/40 text-transparent"
                              }`}
                            >
                              ✓
                            </span>
                            <span className="text-sm sm:text-base font-light text-text-pure">
                              {opcion.label}
                            </span>
                          </div>

                          {haVotado && (
                            <span className="font-mono text-xs sm:text-sm text-gold font-medium whitespace-nowrap">
                              {porcentaje}% ({votosOpcion})
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {haVotado && (
                  <p className="text-center text-[11px] text-text-muted/70 mt-6 font-light">
                    Tu voto ha sido registrado. Gracias por contribuir a visibilizar las prioridades de la Escuela.
                  </p>
                )}
              </section>
            </FadeIn>

            {/* BUZÓN DE PROPUESTAS */}
            <FadeIn delay={0.3}>
              <section id="buzon" className="p-8 sm:p-12 md:p-16 rounded-2xl border border-gold/30 bg-[#090c17] shadow-2xl relative">
                <div className="text-center flex flex-col items-center mb-10">
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Participación Directa
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl text-text-pure mt-3 font-light">
                    Buzón de Ideas y Problemas
                  </h2>
                  <p className="text-text-muted text-xs sm:text-sm font-light max-w-xl mt-2 leading-relaxed">
                    ¿Qué problema ves o qué propones para la Escuela? Puedes enviar tu inquietud de forma identificada o totalmente anónima.
                  </p>
                  <div className="w-12 h-[1px] bg-gold/50 mt-4" />
                </div>

                {buzonStatus === "success" ? (
                  <div className="p-8 rounded-xl bg-gold/10 border border-gold/30 text-center space-y-4 animate-fade-in max-w-xl mx-auto">
                    <div className="w-14 h-14 mx-auto rounded-full bg-gold/20 flex items-center justify-center text-gold text-2xl">
                      ✓
                    </div>
                    <h3 className="font-serif text-xl text-text-pure font-light">
                      ¡Propuesta Recibida!
                    </h3>
                    <p className="text-text-muted text-sm font-light leading-relaxed">
                      Muchas gracias por tomarte el tiempo. Tu propuesta será leída con detenimiento y tomada en cuenta para estructurar iniciativas concretas para los estudiantes.
                    </p>
                    <button
                      onClick={() => setBuzonStatus("idle")}
                      className="border border-gold/40 text-gold text-xs font-mono uppercase px-6 py-2 rounded hover:bg-gold/10 transition-colors"
                    >
                      Enviar otra propuesta
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleEnviarPropuesta} className="space-y-6 max-w-2xl mx-auto text-left">
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="_honey"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      className="hidden"
                      tabIndex={-1}
                      autoComplete="off"
                    />

                    <div>
                      <label className="block text-xs font-mono uppercase text-gold/80 mb-2">
                        ¿Qué problema ves o qué propones? *
                      </label>
                      <textarea
                        value={propuesta}
                        onChange={(e) => setPropuesta(e.target.value)}
                        required
                        rows={5}
                        placeholder="Describe la situación, carencia o propuesta de mejora concreta para nuestra Escuela de Filosofía..."
                        className="w-full px-4 py-3 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm outline-none rounded font-light"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                      <div>
                        <label className="block text-xs font-mono uppercase text-gold/80 mb-2">
                          Ciclo o Condición (Opcional)
                        </label>
                        <input
                          type="text"
                          value={ciclo}
                          onChange={(e) => setCiclo(e.target.value)}
                          placeholder="Ej. 2do ciclo / Egresado / Postulante"
                          className="w-full px-4 py-3 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm outline-none rounded font-light"
                        />
                      </div>

                      <div className="pt-6 sm:pt-4">
                        <label className="flex items-center gap-3 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={esAnonimo}
                            onChange={(e) => setEsAnonimo(e.target.checked)}
                            className="h-4 w-4 rounded border-gold/30 bg-bg-main text-gold focus:ring-gold"
                          />
                          <span className="text-xs text-text-pure font-light">
                            Enviar de forma 100% anónima
                          </span>
                        </label>
                      </div>
                    </div>

                    {buzonError && (
                      <p className="text-red-400 text-xs text-center font-light">{buzonError}</p>
                    )}

                    <div className="text-center pt-4">
                      <button
                        type="submit"
                        disabled={buzonStatus === "loading"}
                        className="w-full sm:w-auto bg-gold text-bg-main hover:bg-gold-light hover:shadow-xl hover:shadow-gold/20 px-10 py-3.5 font-sans font-semibold text-xs uppercase tracking-widest transition-all duration-300 rounded disabled:opacity-50"
                      >
                        {buzonStatus === "loading" ? "Enviando..." : "Enviar al Buzón"}
                      </button>
                    </div>
                  </form>
                )}
              </section>
            </FadeIn>

            {/* SECCIÓN ELECTORAL / CAMPAÑA (OCULTA TRAS FLAG campanaActiva) */}
            {siteConfig.flags.campanaActiva && (
              <FadeIn delay={0.4}>
                <section className="p-8 sm:p-12 rounded-2xl border border-gold/30 bg-bg-secondary/40">
                  <h3 className="font-serif text-2xl text-gold mb-4 font-light">
                    Material Informativo del Proceso Electoral
                  </h3>
                  <p className="text-sm text-text-muted font-light leading-relaxed">
                    Esta sección se encuentra temporalmente en pausa mientras se define la convocatoria electoral oficial.
                  </p>
                </section>
              </FadeIn>
            )}

          </div>
        </main>

        <Footer />
      </div>
    </PageTransition>
  );
}
